import { ElementRef, Injectable, Type } from "@angular/core";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

@Injectable()
export class DynamicDialogUtils {

    static DIALOG_WIDTH = "1000px";

    private dialogRefMap: Map<Type<any>, DynamicDialogRef> = new Map();

    constructor(private dialogService: DialogService) {
    }

    /**
     * Opens dialog
     * @param parentElement element in which the dialog is opened
     * @param component what component is shown in the dialog
     * @param config dialog config
     * @returns dialog
     */
    open(parentElement: ElementRef, component: Type<any>, config: DynamicDialogConfig): DynamicDialogRef {
        if (!config.appendTo && parentElement) {
            config.appendTo = this.getParentElementDialogContainer(parentElement);
        }

        if (!config.width) {
            config.width = DynamicDialogUtils.DIALOG_WIDTH;
        }

        config.closable = true;
        config.modal = true;
        config.focusOnShow = false;
    
        // if the dialog is already open, delete it from ref maps
        if (this.dialogRefMap.has(component)) {
            const oldRef = this.dialogRefMap.get(component);
            this.dialogService.dialogComponentRefMap.delete(oldRef!);
            this.dialogRefMap.delete(component);
        }

        const ref = this.dialogService.open(component, config)!;
        
        ref.onClose.subscribe(() => {
            this.dialogRefMap.delete(component);
        });

        this.dialogRefMap.set(component, ref);

        return ref;
    }

    /**
     * Gets element, dialog container, to which the dialog will be appended.
     * If the dialog container already exists, returns that, otherwise creates a new one.
     * @param parentElement element in which the dialog container is created or returned from
     * @returns dialog container element
     */
    private getParentElementDialogContainer(parentElement: ElementRef): HTMLElement {
        const dynamicDialogContainerAttr = 'appDynamicDialogContainer';
        const dialogContainerList: NodeListOf<any> = parentElement.nativeElement.querySelectorAll(`[${dynamicDialogContainerAttr}]`);

        let dialogContainer: HTMLDivElement;

        if (!dialogContainerList.length) {
            dialogContainer = document.createElement('div');
            dialogContainer.setAttribute(dynamicDialogContainerAttr, '');
            parentElement.nativeElement.appendChild(dialogContainer);
        }
        else {
            dialogContainer = dialogContainerList[0];
        }

        return dialogContainer;
    }
}